/**
 * Customize to add registrationDateTime on PersonInfo
 */

import { useFlexFlowStoreFeature } from '@/data/Content/FlexFlowStoreFeature';
import { useNotifications } from '@/data/Content/Notifications';
import { usePathNameByLocale } from '@/data/Content/PathNameByLocale';
import { usePrivacyAndMarketing } from '@/data/Content/PrivacyAndMarketing';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import {
	selfFetcher,
	selfUpdater,
	UPDATE_USER_REGISTRATION_QUERY,
	UPDATE_USER_REGISTRATION_QUERY_NO_BYPASS,
} from '@/data/Content/_Person';
import { avsContactUpdateOrCreate } from '@/data/Content/_PersonContactFetcher';
import { useLocalization } from '@/data/Localization';
import { dFix, useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { User } from '@/data/_User';
import { DATA_KEY_PERSON } from '@/data/constants/dataKey';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import {
	MARKETING_TRACKING_CONSENT_KEY,
	PRIVACY_NOTICE_VERSION_KEY,
} from '@/data/constants/privacyPolicy';
import { Address } from '@/data/types/Address';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { CONFIGURATION_IDS, LanguageConfiguration } from '@/data/types/Configuration';
import {
	EditablePersonInfo,
	EditablePersonInfoParam,
	Person,
	PersonContact,
} from '@/data/types/Person';
import { isMappedAddressInfoArray } from '@/data/utils/contact';
import { personalContactInfoMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/personalContactInfoMutatorKeyMatcher';
import { isErrorType, processError } from '@/data/utils/processError';
import { SelectChangeEvent } from '@mui/material';
import {
	JavaUtilMapEntry,
	PersonPerson,
	PersonPersonAttributes,
} from 'integration/generated/transactions/data-contracts';
import { isUndefined } from 'lodash';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { useCallback, useMemo, useState } from 'react';
import useSWR, { mutate } from 'swr';

export type { EditablePersonInfo, EditablePersonInfoParam };
export const PRINCIPAL_ADDRESS_TYPE = 'SB';

export type ChangePasswordValues = {
	xcred_logonPasswordOld: string;
	logonPassword: string;
	xcred_logonPasswordVerify: string;
};

/**
 * Custom:
 * Adding address validation here to remove incorrect address that has wrong email1,
 * or empty addressLine.
 * Meanwhile, supply addressLine1, .., addressLine3 for unifying usage.
 * Avoid individual validation implemented by other components.
 */
const dataMapAddresses = (data?: PersonPerson, user?: User) => {
	const { contact = [], ...rest } = data ?? {};
	const flattedAddresses = [data ? rest : null, contact].flat(1).filter(Boolean) as Address[];

	return flattedAddresses
		.filter((address) => {
			if (user?.isLoggedIn && address.email1 !== user.logonId) {
				// has correct email1
				return false;
			}
			if (
				address.nickName?.startsWith('VISA_') ||
				address.nickName?.startsWith('Master Card_') ||
				address.nickName?.startsWith('AMEX_') ||
				address.nickName?.startsWith('CARNET_')
			) {
				return false;
			}
			return !!address.addressLine?.[0] || address.nickName === address.email1;
		})
		.map((address) => ({
			...address,
			addressLine1: address.addressLine?.[0],
			addressLine2: address.addressLine?.[1],
			addressLine3: address.addressLine?.[2],
		}));
};
const dataMap = ({
	data,
	storeId,
	supportedLanguages,
}: {
	data?: Person;
	storeId: string;
	supportedLanguages: LanguageConfiguration[];
}): EditablePersonInfo => {
	const {
		firstName = '',
		lastName = '',
		email1 = '',
		addressLine = ['', '', ''],
		city = '',
		country = '',
		state = '',
		zipCode = '',
		phone1 = '',
		orgizationId: parentOrgId,
		preferredCurrency = '',
		preferredLanguage: preLang = '',
		profileType,
	} = data ?? {};
	const preferredLanguage =
		supportedLanguages.find((lang) => lang.localeName.replace('-', '_') === preLang.toLowerCase())
			?.languageId ?? '';
	const marketingTrackingConsentValue = data?.contextAttribute
		?.find((attribute) => attribute.attributeName === MARKETING_TRACKING_CONSENT_KEY)
		?.attributeValue?.find((value) => value.storeId === storeId)?.value[0];

	const attributesArray: PersonPersonAttributes[] = data?.attributes ?? [];

	const attributes: Record<string, string> = attributesArray.reduce((acc, item) => {
		if (item.contactInfoAttrKey) {
			acc[item.contactInfoAttrKey] = item.contactInfoAttrValue ?? '';
		}
		return acc;
	}, {} as Record<string, string>);

	return {
		firstName,
		lastName,
		email1,
		addressLine1: addressLine[0] ?? '',
		addressLine2: addressLine[1] ?? '',
		addressLine3: addressLine[2] ?? '',
		...attributes,
		city,
		country,
		state,
		zipCode,
		phone1,
		parentOrgId,
		preferredCurrency,
		preferredLanguage,
		privacyNoticeVersion: data?.contextAttribute
			?.find((attribute) => attribute.attributeName === PRIVACY_NOTICE_VERSION_KEY)
			?.attributeValue?.find((value) => value.storeId === storeId)?.value[0],
		...(marketingTrackingConsentValue && {
			marketingTrackingConsent: marketingTrackingConsentValue === '1',
		}),
		nickName: email1,
		addressType: PRINCIPAL_ADDRESS_TYPE,
		addressId: '0',
		profileType,
	};
};

const emptyConfiguration = [] as LanguageConfiguration[];

export const usePersonInfo = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { query } = router;
	const { path, ...restQuery } = query;
	const {
		storeId = '',
		[CONFIGURATION_IDS.SUPPORTED_LANGUAGES]: supportedLanguages = emptyConfiguration,
	} = settings;
	const params = useExtraRequestParameters();
	const personalInformationNLS = useLocalization('PersonalInformation');
	const { showSuccessMessage, notifyError } = useNotifications();
	const [editing, setEditing] = useState(false);
	const [editingInformation, setEditingInformation] = useState(false);
	const { user } = useUser();
	const [editingAddress, setEditingAddress] = useState(false);
	const [changePassword, setChangePassword] = useState(false);
	const [selectedLocale, setSelectedLocale] = useState('');
	const { privacyNoticeVersion, setMarketingTrackingConsent, setPrivacyNoticeVersion } =
		usePrivacyAndMarketing();
	const { data: _marketingConsent } = useFlexFlowStoreFeature({
		id: EMS_STORE_FEATURE.MARKETING_CONSENT,
	});
	const { featureEnabled: marketingConsentFeature } = _marketingConsent;
	const key: [{ storeId: string }, string] = useMemo(
		() => [{ storeId: settings.storeId ?? '' }, DATA_KEY_PERSON],
		[settings.storeId]
	);
	const {
		data,
		error: personInfoError,
		mutate: mutatePersonInfo,
		isLoading,
	} = useSWR(key, async ([props]) => selfFetcher(true)(props.storeId, undefined, params));
	const personPreferredLocale = (data?.preferredLanguage ?? '').replace('_', '-').toLowerCase();
	const addresses = useMemo(() => dataMapAddresses(data, user), [data, user]);
	const [displayAddresses, setDisplayAddresses] = useState<Address[]>(addresses);
	const edit = useCallback(() => {
		setEditing(true);
	}, []);

	const cancelEdit = useCallback(async () => {
		setEditing(false);
	}, []);

	const editInformation = useCallback(() => {
		setEditingInformation(true);
	}, []);

	const editAddress = useCallback(() => {
		setEditingAddress(true);
	}, []);

	const cancelEditInformation = useCallback(() => {
		setEditingInformation(false);
	}, []);

	const cancelEditAddress = useCallback(async () => {
		setEditingAddress(false);
	}, []);

	const newPathname = usePathNameByLocale({ locale: selectedLocale });
	const trackException = useTrackException();
	const changeLocaleIfNeeded = useCallback(async () => {
		if (selectedLocale && selectedLocale !== personPreferredLocale) {
			return await router.push({ pathname: newPathname, query: restQuery }, undefined, {
				locale: selectedLocale,
			});
		}
	}, [newPathname, personPreferredLocale, restQuery, router, selectedLocale]);

	const savePersonInfo = useCallback(
		async (info: EditablePersonInfo) => {
			const {
				addressLine1,
				addressLine2,
				addressLine3,
				marketingTrackingConsent: _consent,
				privacyNoticeVersion: _discard,
				email1,
				..._address
			} = info;
			const privacyData = {
				...(!isUndefined(_consent) && { marketingTrackingConsent: _consent ? '1' : '0' }),
				...(!isUndefined(privacyNoticeVersion) && { privacyNoticeVersion }),
			};
			const data = <EditablePersonInfoParam>{
				address1: addressLine1,
				address2: addressLine2,
				address3: addressLine3,
				addressLine1,
				addressLine2,
				..._address,
				email1: email1?.toLowerCase(),
				...privacyData,
			};
			try {
				const resp = await selfUpdater(true)(storeId, UPDATE_USER_REGISTRATION_QUERY, data, params);
				if (isMappedAddressInfoArray(resp)) {
					return {
						validatedAddresses: resp,
						editingAddress: info,
						callback: cancelEdit,
					};
				}
				setEditing(false);
				showSuccessMessage(personalInformationNLS.UpdateSuccessful.t());
				privacyData.privacyNoticeVersion &&
					setPrivacyNoticeVersion(dFix(privacyData.privacyNoticeVersion));
				privacyData.marketingTrackingConsent &&
					setMarketingTrackingConsent(dFix(privacyData.marketingTrackingConsent));
				await changeLocaleIfNeeded();
				mutate(personalContactInfoMutatorKeyMatcher(''), undefined);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
				trackException(e);
			}
		},
		[
			privacyNoticeVersion,
			storeId,
			params,
			showSuccessMessage,
			personalInformationNLS.UpdateSuccessful,
			setPrivacyNoticeVersion,
			setMarketingTrackingConsent,
			changeLocaleIfNeeded,
			cancelEdit,
			notifyError,
			trackException,
		]
	);

	const savePersonInformation = useCallback(
		async (info: EditablePersonInfo) => {
			const { firstName, lastName, email1, phone1 } = info;

			const data = <EditablePersonInfoParam>{
				firstName,
				lastName,
				email1: email1?.toLowerCase(),
				phone1,
				addressType: PRINCIPAL_ADDRESS_TYPE,
				editRegistration: 'Y',
				receiveEmail: 'false',
			};
			try {
				await selfUpdater(true)(storeId, UPDATE_USER_REGISTRATION_QUERY_NO_BYPASS, data, params);
				setEditingInformation(false);
				showSuccessMessage(personalInformationNLS.UpdateSuccessful.t());
				mutate(personalContactInfoMutatorKeyMatcher(''), undefined);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
				trackException(e);
			}
		},
		[
			storeId,
			params,
			showSuccessMessage,
			personalInformationNLS.UpdateSuccessful,
			notifyError,
			trackException,
		]
	);

	const savePersonAddress = useCallback(
		async (info: EditablePersonInfo) => {
			const {
				addressLine1,
				addressLine2,
				addressLine3,
				addressField1,
				addressField3,
				city,
				country,
				state,
				zipCode,
			} = info;

			const data = <EditablePersonInfoParam>{
				address1: addressLine1,
				address2: addressLine2 ?? '',
				address3: addressLine3 ?? '',
				addressField1: addressField1 ?? '',
				addressField3: addressField3 ?? '',
				city,
				country,
				state,
				zipCode,
				addressType: PRINCIPAL_ADDRESS_TYPE,
				editRegistration: 'Y',
				receiveEmail: 'false',
			};
			try {
				const resp = await selfUpdater(true)(
					storeId,
					UPDATE_USER_REGISTRATION_QUERY_NO_BYPASS,
					data,
					params
				);
				if (isMappedAddressInfoArray(resp)) {
					return {
						validatedAddresses: resp,
						editingAddress: info,
						callback: cancelEditAddress,
					};
				}
				setEditingAddress(false);
				showSuccessMessage(personalInformationNLS.UpdateSuccessful.t());
				mutate(personalContactInfoMutatorKeyMatcher(''), undefined);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
				trackException(e);
			}
		},
		[
			storeId,
			params,
			showSuccessMessage,
			personalInformationNLS.UpdateSuccessful,
			cancelEditAddress,
			notifyError,
			trackException,
		]
	);

	const closePasswordDialog = useCallback(() => {
		setChangePassword(false);
	}, []);

	const openPasswordDialog = useCallback(() => {
		setChangePassword(true);
	}, []);
	const [isSettingPrimary, setIsSettingPrimary] = useState(false);

	const updatePassword = useCallback(
		async (value: ChangePasswordValues) => {
			const data = { resetPassword: 'true', ...value };
			try {
				await selfUpdater(true)(storeId, undefined, data, params);
				setChangePassword(false);
				showSuccessMessage(personalInformationNLS.UpdateSuccessful.t());
			} catch (e) {
				const error = processError(e as TransactionErrorResponse);
				notifyError(error);
				trackException(e);
				if (isErrorType(error)) {
					return error.error.errorCode;
				}
			}
		},
		[
			notifyError,
			personalInformationNLS.UpdateSuccessful,
			storeId,
			showSuccessMessage,
			params,
			trackException,
		]
	);
	const setPrimaryAddress = useCallback(
		async (nickName: string, isFromDelete?: boolean) => {
			if (isSettingPrimary) return;
			if (!nickName) {
				return;
			}
			const prevDisplay = displayAddresses;

			setDisplayAddresses((prev) =>
				prev.map((a) => ({
					...a,
					primary: a.nickName === nickName ? 'true' : 'false',
				}))
			);

			const previousPrimary = displayAddresses.find((a) => a.primary === 'true');
			const current = displayAddresses.find((a) => a.nickName === nickName);

			if (!current) {
				setDisplayAddresses(prevDisplay);
				return;
			}

			const storeId = settings?.storeId ?? '';
			const query = { bypassAVS: 'false' };

			const removeAsPrimaryData = {
				...previousPrimary,
				primary: 'false',
				addressLine: previousPrimary?.addressLine,
				attributes: previousPrimary?.attributes?.filter(
					(att): att is JavaUtilMapEntry => 'key' in att
				),
			};

			const data = {
				...current,
				primary: 'true',
				addressLine: current.addressLine,
				attributes: current.attributes?.filter((att): att is JavaUtilMapEntry => 'key' in att),
			};

			try {
				setIsSettingPrimary(true);
				if (!!removeAsPrimaryData.nickName && !isFromDelete) {
					await avsContactUpdateOrCreate(
						true,
						!!removeAsPrimaryData.addressId
					)({
						storeId,
						nickName: removeAsPrimaryData.nickName,
						query,
						data: removeAsPrimaryData,
						params,
					});
				}

				await avsContactUpdateOrCreate(
					true,
					!!current.addressId
				)({
					storeId,
					nickName,
					query,
					data,
					params,
				});

				await mutatePersonInfo();
				await mutate(personalContactInfoMutatorKeyMatcher(key), undefined);
			} catch (e) {
				setDisplayAddresses(prevDisplay);
				notifyError(processError(e as TransactionErrorResponse));
				trackException(e);
			} finally {
				setIsSettingPrimary(false);
			}
		},
		[
			isSettingPrimary,
			displayAddresses,
			settings?.storeId,
			params,
			mutatePersonInfo,
			key,
			notifyError,
			trackException,
		]
	);
	const { personInfo, primaryAddress, registrationDateTime } = useMemo(() => {
		const personInfo = dataMap({ data, storeId, supportedLanguages });
		if (marketingConsentFeature && personInfo.marketingTrackingConsent === undefined) {
			personInfo.marketingTrackingConsent = false;
		}
		const { nickName, addressLine, addressType, addressId, registrationDateTime, profileType } =
			data ?? {};
		const primaryAddress = nickName
			? ({
					...personInfo,
					addressId,
					addressLine,
					nickName,
					addressType,
					primary: 'true',
					profileType,
			  } as PersonContact)
			: undefined;
		return { personInfo, primaryAddress, registrationDateTime };
	}, [marketingConsentFeature, data, storeId, supportedLanguages]);

	const handlePreferredLanguageSelectChange = useCallback(
		// callback is handleselectchange from useForm
		(callback: (event: SelectChangeEvent) => void) => (event: SelectChangeEvent) => {
			const elm = event.target;
			const { name, value } = elm;
			if (name === 'preferredLanguage' && value !== '') {
				const newLocale =
					supportedLanguages.find((lang) => lang.languageId === value)?.localeName ?? '';
				setSelectedLocale(newLocale);
			}
			return callback(event);
		},
		[supportedLanguages]
	);

	return {
		personSelf: data,
		setPrimaryAddress,
		setDisplayAddresses,
		displayAddresses,
		addresses,
		personInfo,
		primaryAddress,
		mutatePersonInfo,
		personInfoError,
		editing,
		editingInformation,
		editingAddress,
		edit,
		editInformation,
		editAddress,
		cancelEdit,
		cancelEditInformation,
		cancelEditAddress,
		savePersonInfo,
		savePersonInformation,
		savePersonAddress,
		updatePassword,
		closePasswordDialog,
		openPasswordDialog,
		changePassword,
		handlePreferredLanguageSelectChange,
		isLoading,
		registrationDateTime,
	};
};
